import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { QuillEditor } from "../../utils/quill-editor";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { EmployeePickerSheet } from "../hours/hourFormPickers";
import { AbsenceTypePickerSheet } from "./absencePickers";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { createAbsence, fetchAbsence, updateAbsence } from "../../../api/absence/absenece";
import { PMDatePicker } from "../../utils/PMDatePicker";
import {
  absenceMaySpanPublicHolidays,
  absencePickerMaxDays,
  isAbsenceEndDateValid,
  isSickLeaveAbsenceType,
  isUnlimitedAbsenceDuration,
} from "./absenceDateRules";
import { fetchAbsenceType } from "../../../api/absence/type";
import { fetchHolidays, holidaysByEffectiveDate } from "../../../api/holiday";
import { PMNotice } from "../../utils/notifications";
import { DocumentCenter } from "../documents/document-center";
import { uploadModuleDocuments } from "../../../api/moduleDocumentUpload";

// ── Promang brand/design tokens & helpers (mirrors hour_form.js) ─────────────
const ACCENT = {
  solid: "#1463C7",
  grad: "linear-gradient(135deg,#1463C7,#2D8BE8)",
  deep: "#0B3A78",
  mid: "#2D8BE8",
  heroBg: "linear-gradient(160deg,#EAF3FD,#F2F8FE)",
  heroBorder: "#BFDCF7",
  soft: "#EAF3FD",
  glow: "rgba(20,99,199,",
};

const AVATAR_COLORS = ["#4F46E5", "#0D9488", "#D946EF", "#F59E0B", "#0EA5E9", "#10B981", "#1463C7"];
const avatarColor = (key) => {
  const s = String(key ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// Shared design style fragments
const dsLabelStyle = { display: "block", fontSize: 13, fontWeight: 700, color: "#334155", margin: "0 0 9px" };
const dsFieldCardStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 16px",
  border: "1.5px solid #E2E8F0",
  borderRadius: 14,
  background: "#fff",
  fontFamily: "inherit",
  textAlign: "left",
};
const dsHintStyle = { marginTop: 6, fontSize: 12.5, color: "#94A3B8", fontWeight: 600 };

const DsChevron = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#94A3B8"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

// Pre-save document staging (create mode). Absence attachments are PDF-only,
// matching the DocumentCenter config used in edit mode.
const ABSENCE_PENDING_DOC_ACCEPT = "application/pdf";
const isAbsencePendingDocAllowed = (file) =>
  (file?.type || "").toLowerCase().trim() === "application/pdf";

const formatBytes = (b) => {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1048576).toFixed(1).replace(".", ",")} MB`;
};

// Long, capitalized Norwegian date for the header subtitle (matches hour_form.js).
const formatLongDateNb = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${String(dateStr).substring(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const s = d.toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatShortDateNb = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${String(dateStr).substring(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" });
};

const formatPeriodLabel = (from, to) => {
  if (!from) return "—";
  if (!to || to === from) return formatLongDateNb(from);
  return `${formatShortDateNb(from)} – ${formatShortDateNb(to)}`;
};

/**
 * Self-contained absence create/edit form, styled to the Promang brand design
 * (mirrors hour_form.js). Used both by the editAbsence page and the dashboard
 * "+" quick-add dialog.
 *
 * Props:
 * - absenceId: id to edit (null/undefined => create mode)
 * - prefill: { user_id, date_from, date_too, description } for create mode
 * - onChange(savedAbsence): called after a successful save
 * - onLoaded(absence|null): called whenever the loaded absence changes (page chrome)
 * - onClose(): called by the cancel button
 * - showHeader: render the in-form header (title + selected date). Use in the
 *   dialog context; the editAbsence page keeps its own DashboardHeader instead.
 */
export const AbsenceForm = ({ absenceId = null, prefill = null, onChange = null, onLoaded = null, onClose = null, showHeader = false }) => {
  const { t } = useTranslation();

  const isAdmin = useUserHasAccess({ role: "absence", action: "approve" });
  const currentUser = useSelector((state) => state.session.currentUser);
  const isSuperAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const dateToday = new Date().toISOString().split("T")[0];

  const [isLoading, setIsLoading] = useState(Boolean(absenceId));
  const [absence, setAbsence] = useState(null);
  const [selectedAbsenceType, setSelectedAbsenceType] = useState(null);
  const [absenceTypeSelectorOpen, setAbsenceTypeSelectorOpen] = useState(false);
  const [employeeSelectorOpen, setEmployeeSelectorOpen] = useState(false);
  const [holidayByDate, setHolidayByDate] = useState(() => new Map());
  const [errors, setErrors] = useState([]);
  const [pendingAbsenceDocuments, setPendingAbsenceDocuments] = useState([]);
  const [savedOverlay, setSavedOverlay] = useState(false);
  const [savedSummary, setSavedSummary] = useState(null);
  const loadedHolidayYearsRef = useRef(new Set());
  const isUserSelectingRef = useRef(false);
  const pendingAbsenceDocInputRef = useRef(null);
  const absenceTypeButtonRef = useRef(null);
  const lastSavedAbsenceRef = useRef(null);
  const [initialFocusKey, setInitialFocusKey] = useState(0);

  const isApproved = absence?.accepted || absence?.status === "approved";
  const isReadOnly = isApproved && !isAdmin; // Only admins can edit approved absences

  const [initialValues, setInitialValues] = useState({
    id: null,
    description: prefill?.description || "",
    absence_type_id: null,
    user_id: prefill?.user_id || currentUser?.id || null,
    date_from: prefill?.date_from || prefill?.date || dateToday,
    date_too: prefill?.date_too || "",
    submit: null,
  });

  const mergeHolidayMap = (monthHolidays) => {
    if (!monthHolidays || monthHolidays.size === 0) return;
    setHolidayByDate((prev) => {
      const next = new Map(prev);
      monthHolidays.forEach((holiday, dateKey) => next.set(dateKey, holiday));
      return next;
    });
  };

  const ensureHolidaysForYear = async (year) => {
    if (!year || loadedHolidayYearsRef.current.has(year)) return;
    loadedHolidayYearsRef.current.add(year);
    try {
      const res = await fetchHolidays({
        date_from: `${year}-01-01`,
        date_to: `${year}-12-31`,
      });
      mergeHolidayMap(holidaysByEffectiveDate(res?.data || []));
    } catch {
      loadedHolidayYearsRef.current.delete(year);
    }
  };

  const isPublicHolidayDate = (dateStr) => {
    if (!dateStr) return false;
    return holidayByDate.has(String(dateStr).substring(0, 10));
  };

  const rangeIncludesPublicHoliday = (from, to) => {
    if (!from || !to) return isPublicHolidayDate(from);
    const start = new Date(`${from}T12:00:00`);
    const end = new Date(`${to}T12:00:00`);
    if (start > end) return isPublicHolidayDate(from);
    const current = new Date(start);
    while (current <= end) {
      const key = current.toISOString().split("T")[0];
      if (holidayByDate.has(key)) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  };

  const holidaysInSelectedRange = (from, to) => {
    if (!from) return [];
    const endStr = to || from;
    const start = new Date(`${from}T12:00:00`);
    const end = new Date(`${endStr}T12:00:00`);
    if (start > end) return [];
    const found = [];
    const current = new Date(start);
    while (current <= end) {
      const key = current.toISOString().split("T")[0];
      const holiday = holidayByDate.get(key);
      if (holiday) found.push(holiday);
      current.setDate(current.getDate() + 1);
    }
    return found;
  };

  const holidayDayInfoMap = Object.fromEntries(
    [...holidayByDate.entries()].map(([date, holiday]) => [date, holiday.name])
  );

  const getAbsence = async (id) => {
    setIsLoading(true);
    try {
      const result = await fetchAbsence(id);
      if (result.success && result.data) {
        setAbsence(result.data);
      } else {
        toast.error(result.errors?.join(", ") || t("absence.edit.load_failed"));
      }
    } catch (err) {
      toast.error(t("absence.edit.load_error"));
    } finally {
      setIsLoading(false);
    }
  };

  // Load (edit) or reset (create) when the target id changes
  useEffect(() => {
    if (absenceId) {
      getAbsence(absenceId);
    } else {
      setIsLoading(false);
      setAbsence(null);
      setInitialValues({
        id: null,
        description: prefill?.description || "",
        absence_type_id: null,
        user_id: prefill?.user_id || currentUser?.id || null,
        date_from: prefill?.date_from || prefill?.date || dateToday,
        date_too: prefill?.date_too || "",
        submit: null,
      });
      setSelectedAbsenceType(null);
      setPendingAbsenceDocuments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absenceId]);

  // Notify parent (page chrome) whenever the loaded absence changes
  useEffect(() => {
    if (onLoaded) onLoaded(absence);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absence]);

  useEffect(() => {
    if (absence && absence.id) {
      const absenceTypeId = absence.absence_type?.id || absence.absence_type_id;

      setInitialValues({
        id: absence.id,
        description: absence.description || "",
        absence_type_id: absenceTypeId || null,
        user_id: isAdmin && absence.user?.id ? absence.user.id : (isAdmin ? currentUser?.id : absence.user?.id),
        date_from: absence.date_from || dateToday,
        date_too: absence.date_too || "",
        submit: null,
      });

      // Always fetch the full absence_type object so we have all fields (e.g. max_duration_days)
      if (absenceTypeId) {
        fetchAbsenceType(absenceTypeId)
          .then((response) => {
            const absenceTypeData = response?.data || response;
            if (absenceTypeData && absenceTypeData.id) {
              setSelectedAbsenceType(absenceTypeData);
            } else if (absence.absence_type) {
              setSelectedAbsenceType(absence.absence_type);
            }
          })
          .catch(() => {
            if (absence.absence_type) {
              setSelectedAbsenceType(absence.absence_type);
            }
          });
      } else {
        setSelectedAbsenceType(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absence, isAdmin, currentUser?.id, dateToday]);

  // Fallback: fetch absence type details when absence_type_id changes
  useEffect(() => {
    if (isUserSelectingRef.current) {
      isUserSelectingRef.current = false;
      return;
    }

    if (initialValues.absence_type_id && !selectedAbsenceType) {
      fetchAbsenceType(initialValues.absence_type_id)
        .then((response) => {
          const absenceTypeData = response?.data || response;
          if (absenceTypeData && absenceTypeData.id) {
            setSelectedAbsenceType(absenceTypeData);
          }
        })
        .catch(() => {
          // Silently fail - errors are handled elsewhere
        });
    } else if (!initialValues.absence_type_id && selectedAbsenceType && !absence?.id) {
      setSelectedAbsenceType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues.absence_type_id, selectedAbsenceType, absence?.id]);

  useEffect(() => {
    const years = new Set();
    const addYear = (value) => {
      if (!value) return;
      years.add(parseInt(String(value).substring(0, 4), 10));
    };
    addYear(initialValues.date_from);
    addYear(initialValues.date_too);
    if (years.size === 0) years.add(new Date().getFullYear());
    years.forEach((year) => {
      ensureHolidaysForYear(year);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues.date_from, initialValues.date_too]);

  // Clear errors after a while
  useEffect(() => {
    if (errors && errors.length > 0) {
      const timer = setTimeout(() => setErrors([]), 20000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [errors]);

  useEffect(() => {
    if (isLoading || savedOverlay || isReadOnly) return undefined;

    const timer = window.setTimeout(() => {
      const btn = absenceTypeButtonRef.current;
      if (btn && !btn.disabled) {
        btn.focus({ preventScroll: true });
      }
    }, 50);

    return () => window.clearTimeout(timer);
  }, [isLoading, savedOverlay, isReadOnly, absenceId, initialFocusKey]);

  const createOrUpdate = async (data) => {
    const payload = {
      ...data,
      user_id: data.user_id || absence?.user?.id || absence?.user_id,
    };

    if (!payload.user_id) {
      const errorMsg = t("absence.edit.user_id_required");
      setErrors([errorMsg]);
      throw new Error(errorMsg);
    }

    const result = absence?.id
      ? await updateAbsence(payload)
      : await createAbsence(payload, { fields: "*" });

    if (result.success === false) {
      const errorMessages = result.errors || [];
      setErrors(errorMessages);
      throw new Error(errorMessages.join(", "));
    }

    if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
      setErrors(result.errors);
      throw new Error(result.errors.join(", "));
    }

    if (result.data && result.data.errors && Array.isArray(result.data.errors) && result.data.errors.length > 0) {
      setErrors(result.data.errors);
      throw new Error(result.data.errors.join(", "));
    }

    if (!result.data || (result.data.errors && Array.isArray(result.data.errors))) {
      const errorMessages = result.data?.errors || [t("absence.edit.unknown_save_error")];
      setErrors(errorMessages);
      throw new Error(errorMessages.join(", "));
    }

    return result.data;
  };

  const resetFormForNewEntry = (formik) => {
    const freshUserId = prefill?.user_id || currentUser?.id || null;
    const freshDateFrom = prefill?.date_from || dateToday;
    setAbsence(null);
    setSelectedAbsenceType(null);
    setPendingAbsenceDocuments([]);
    setErrors([]);
    const freshValues = {
      id: null,
      description: "",
      absence_type_id: null,
      user_id: freshUserId,
      date_from: freshDateFrom,
      date_too: "",
      submit: null,
    };
    setInitialValues(freshValues);
    setInitialFocusKey((key) => key + 1);
    if (formik) formik.resetForm({ values: freshValues });
  };

  const canvasStyle = {
    background: "#F8FAFC",
    borderRadius: 16,
    padding: 16,
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 20,
  };

  if (isLoading) return <ResourceLoading />;

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", width: "100%" }}>
      <Formik
        enableReinitialize={true}
        initialValues={{
          ...initialValues,
          user_id: initialValues.user_id || (!isAdmin ? currentUser?.id : null),
        }}
        validationSchema={Yup.object({
          description: Yup.string(),
          user_id: Yup.number()
            .required(t("absence.edit.user_required"))
            .nullable(),
        })}
        onSubmit={async (values, helpers) => {
          try {
            setErrors([]);

            if (!values.user_id) {
              helpers.setFieldError("user_id", t("absence.edit.user_required"));
              helpers.setSubmitting(false);
              toast.error(t("absence.edit.user_required_save"));
              return;
            }

            const rangeHolidays = holidaysInSelectedRange(values.date_from, values.date_too);
            if (rangeHolidays.length > 0 && !absenceMaySpanPublicHolidays(selectedAbsenceType)) {
              const names = [...new Set(rangeHolidays.map((h) => h.name))].join(", ");
              toast.error(t("absence.form.public_holiday_blocked", { names }));
              helpers.setSubmitting(false);
              return;
            }

            const data = await createOrUpdate(values);

            if (!data || typeof data !== 'object') {
              helpers.setSubmitting(false);
              return;
            }

            if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
              setErrors(data.errors);
              helpers.setSubmitting(false);
              return;
            }

            if (!data.id && !absence?.id) {
              helpers.setSubmitting(false);
              return;
            }

            // Associate any documents staged before save (create mode) by
            // uploading them to the freshly-created absence id before we close
            // / refresh. Mirrors the hour_form.js pending-upload pattern.
            const wasCreate = !absence?.id;
            let uploadedCount = 0;
            if (wasCreate && pendingAbsenceDocuments.length > 0 && data?.id) {
              try {
                await uploadModuleDocuments(
                  "absence",
                  data.id,
                  pendingAbsenceDocuments.map((p) => p.file)
                );
                uploadedCount = pendingAbsenceDocuments.length;
                setPendingAbsenceDocuments([]);
              } catch (uploadErr) {
                toast.error(t("absence.edit.documents_upload_failed"));
              }
            }

            setAbsence(data);
            setInitialValues((prev) => ({
              ...prev,
              ...values,
            }));
            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
            setErrors([]);

            if (wasCreate) {
              // Defer onChange until the user dismisses the success overlay —
              // parents close the dialog on onChange, which would unmount it.
              lastSavedAbsenceRef.current = data;
              const employeeObj =
                data?.user ||
                (values.user_id && String(currentUser?.id) === String(values.user_id) ? currentUser : null);
              const typeObj =
                selectedAbsenceType ||
                data?.absence_type ||
                (values.absence_type_id ? { id: values.absence_type_id } : null);
              setSavedSummary({
                typeLabel: typeObj?.displayTitle || typeObj?.title || "—",
                employeeLabel: employeeObj?.displayTitle || employeeObj?.name || "—",
                periodLabel: formatPeriodLabel(values.date_from, values.date_too),
                showEmployee: isAdmin,
                hadDocuments: uploadedCount > 0,
              });
              setSavedOverlay(true);
              if (uploadedCount > 0) {
                toast.success(t("absence.edit.documents_uploaded"));
              }
              if (onLoaded) onLoaded(data);
              return;
            }

            toast.success(t("absence.edit.saved"));
            if (uploadedCount > 0) toast.success(t("absence.edit.documents_uploaded"));
            if (onChange) onChange(data);
          } catch (err) {
            helpers.setSubmitting(false);
            // Errors are already set in createOrUpdate
          }
        }}
      >
        {(formik) => {
          const rangeHolidays = holidaysInSelectedRange(
            formik.values.date_from,
            formik.values.date_too
          );
          const holidayNames = [...new Set(rangeHolidays.map((h) => h.name))].join(", ");

          // Resolve the selected employee like hour_form.js (match the id against
          // known objects; fall back to "select" instead of a perpetual loading state).
          const resolveEmployee = () => {
            const uid = formik.values.user_id;
            if (uid == null || uid === "") return null;
            if (absence?.user && String(absence.user.id) === String(uid)) return absence.user;
            if (currentUser && String(currentUser.id) === String(uid)) return currentUser;
            return { id: uid };
          };
          const currentEmployee = resolveEmployee();
          const employeeName =
            currentEmployee?.displayTitle || currentEmployee?.name || t("absence.edit.select_employee");
          const readOnlyEmployeeName =
            absence?.user?.displayTitle || absence?.user?.name || currentUser?.displayTitle || currentUser?.name || "—";

          const datesDisabled = !formik.values.user_id || !formik.values.absence_type_id || isReadOnly;
          const canSave = !!formik.values.user_id && !isReadOnly;

          const saveBtnBase = {
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: 16,
            borderRadius: 14,
            border: "none",
            fontFamily: "inherit",
            fontSize: 16,
            fontWeight: 800,
            transition: "all .2s",
          };
          const saveBtnStyle = canSave && !formik.isSubmitting
            ? { ...saveBtnBase, color: "#fff", background: ACCENT.grad, boxShadow: `0 12px 24px -8px ${ACCENT.glow}.6)`, cursor: "pointer" }
            : { ...saveBtnBase, background: "#E2E8F0", color: "#94A3B8", cursor: "not-allowed", boxShadow: "none" };

            return (
              <>
              <Form>
                {showHeader && (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                    {onClose && (
                      <button
                        type="button"
                        onClick={() => onClose()}
                        aria-label={t("absence.edit.close")}
                        style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", border: "none", background: "#F1F5F9", borderRadius: 11, cursor: "pointer", color: "#475569", flex: "0 0 auto" }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                      </button>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A", letterSpacing: "-.01em" }}>
                        {absence?.id ? t("absence.edit.title_edit") : t("absence.edit.title_new")}
                      </div>
                      <div style={{ fontSize: 12.5, color: "#94A3B8", fontWeight: 600, textTransform: "capitalize", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {formatLongDateNb(formik.values.date_from)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {errors && errors.length > 0 && (
                <div className="mb-4">
                  <PMNotice
                    severity="error"
                    title={t("absence.edit.error_title")}
                    description={
                      <ul className="list-disc list-inside space-y-1 mt-2">
                        {errors.map((error, index) => (
                          <li key={index} className="text-sm">
                            {error}
                          </li>
                        ))}
                      </ul>
                    }
                  />
                </div>
              )}

              {isReadOnly && (
                <div className="mb-4">
                  <PMNotice
                    severity="warning"
                    title={t("absence.edit.read_only_title")}
                    description={t("absence.edit.read_only_body")}
                  />
                </div>
              )}

              {rangeHolidays.length > 0 && !absenceMaySpanPublicHolidays(selectedAbsenceType) && (
                <div className="mb-4">
                  <PMNotice
                    severity="warning"
                    title={t("absence.form.public_holiday_title")}
                    description={t("absence.form.public_holiday_body", { names: holidayNames })}
                  />
                </div>
              )}

              <div style={canvasStyle}>
                {/* Ansatt */}
                <div>
                  <label style={dsLabelStyle}>{t("absence.edit.employee")}</label>
                  {isAdmin ? (
                    <div style={{ position: "relative" }}>
                      <button
                        type="button"
                        onClick={() => !isReadOnly && setEmployeeSelectorOpen(true)}
                        disabled={isReadOnly}
                        style={{ ...dsFieldCardStyle, cursor: isReadOnly ? "not-allowed" : "pointer", opacity: isReadOnly ? 0.6 : 1 }}
                      >
                        <span style={{ width: 40, height: 40, borderRadius: 11, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 14, background: avatarColor(currentEmployee?.id || employeeName) }}>
                          {initialsOf(employeeName)}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 16, fontWeight: 700, color: "#0F172A" }}>{employeeName}</span>
                          <span style={{ display: "block", fontSize: 12.5, color: "#94A3B8", fontWeight: 500 }}>{t("absence.edit.employee_admin_only")}</span>
                        </span>
                        <DsChevron />
                      </button>
                      {formik.errors.user_id && formik.touched.user_id && (
                        <p style={{ marginTop: 6, fontSize: 12, color: "#DC2626" }}>{formik.errors.user_id}</p>
                      )}
                      <EmployeePickerSheet
                        open={employeeSelectorOpen}
                        onClose={() => setEmployeeSelectorOpen(false)}
                        selectedId={formik.values.user_id}
                        onSelect={(obj) => {
                          formik.setFieldValue("user_id", obj?.id ?? null);
                          setAbsence((prev) => ({ ...(prev || {}), user: obj || null }));
                          setInitialValues((prev) => ({ ...prev, user_id: obj?.id ?? null }));
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{ ...dsFieldCardStyle, opacity: 0.85 }}>
                      <span style={{ width: 40, height: 40, borderRadius: 11, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 14, background: avatarColor(absence?.user?.id || readOnlyEmployeeName) }}>
                        {initialsOf(readOnlyEmployeeName)}
                      </span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 700, color: "#0F172A" }}>{readOnlyEmployeeName}</span>
                    </div>
                  )}
                </div>

                {/* Fraværstype */}
                <div>
                  <label style={dsLabelStyle}>{t("absence.edit.absence_type")}</label>
                  <div style={{ position: "relative" }}>
                    {(() => {
                      const typeDisabled = !formik.values.user_id || isReadOnly;
                      const typeLabel = selectedAbsenceType
                        ? (selectedAbsenceType.displayTitle || selectedAbsenceType.title)
                        : t("absence.edit.select_absence_type");
                      return (
                        <button
                          ref={absenceTypeButtonRef}
                          type="button"
                          onClick={() => {
                            if (formik.values.user_id && !isReadOnly) {
                              setAbsenceTypeSelectorOpen(true);
                            }
                          }}
                          disabled={typeDisabled}
                          style={{
                            ...dsFieldCardStyle,
                            cursor: typeDisabled ? "not-allowed" : "pointer",
                            ...(formik.values.user_id ? {} : { border: "1.5px solid #EEF1F5", background: "#F1F5F9" }),
                          }}
                        >
                          <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: selectedAbsenceType ? "#0F172A" : "#94A3B8" }}>{typeLabel}</span>
                          <DsChevron />
                        </button>
                      );
                    })()}
                    <AbsenceTypePickerSheet
                      open={absenceTypeSelectorOpen}
                      onClose={() => setAbsenceTypeSelectorOpen(false)}
                      userId={formik.values.user_id || currentUser?.id}
                      selectedId={formik.values.absence_type_id}
                      onSelect={(absenceType) => {
                        if (!absenceType) return;
                        isUserSelectingRef.current = true;
                        formik.setFieldValue("absence_type_id", absenceType.id);
                        setSelectedAbsenceType(absenceType);
                        setInitialValues((prev) => ({
                          ...prev,
                          absence_type_id: absenceType.id,
                        }));
                        if (formik.values.date_from && formik.values.date_too) {
                          const start = new Date(formik.values.date_from);
                          const end = new Date(formik.values.date_too);
                          const diffTime = Math.abs(end - start);
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                          const maxDays = absencePickerMaxDays(absenceType);
                          if (maxDays && !isUnlimitedAbsenceDuration(absenceType) && diffDays > maxDays) {
                            formik.setFieldValue("date_too", "");
                          }
                        }
                      }}
                    />
                  </div>
                  {!formik.values.user_id && (
                    <p style={dsHintStyle}>{t("absence.edit.select_employee_first")}</p>
                  )}
                </div>

                {/* Periode */}
                <div>
                  <label style={dsLabelStyle}>{t("absence.edit.period")}</label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
                    <div>
                      <PMDatePicker
                        triggerVariant="design"
                        accentColor={ACCENT.solid}
                        name="date_from"
                        label={t("Dato fra")}
                        value={formik.values.date_from}
                        disabled={datesDisabled}
                        dayInfoMap={holidayDayInfoMap}
                        onOpen={() => {
                          const year = formik.values.date_from
                            ? parseInt(String(formik.values.date_from).substring(0, 4), 10)
                            : new Date().getFullYear();
                          ensureHolidaysForYear(year);
                        }}
                        onMonthChange={(_month, year) => ensureHolidaysForYear(year)}
                        onChange={(e) => {
                          formik.setFieldValue("date_from", e.target.value);
                          const year = parseInt(String(e.target.value).substring(0, 4), 10);
                          ensureHolidaysForYear(year);
                          if (formik.values.date_too && e.target.value > formik.values.date_too) {
                            formik.setFieldValue("date_too", "");
                          } else if (
                            formik.values.date_too &&
                            !absenceMaySpanPublicHolidays(selectedAbsenceType) &&
                            rangeIncludesPublicHoliday(e.target.value, formik.values.date_too)
                          ) {
                            formik.setFieldValue("date_too", "");
                          }
                        }}
                        validationRules={{
                          minDate: isSuperAdmin || isSickLeaveAbsenceType(selectedAbsenceType) ? undefined : dateToday,
                          startDate: formik.values.date_from,
                          isDateValid: (dateStr) =>
                            absenceMaySpanPublicHolidays(selectedAbsenceType) || !isPublicHolidayDate(dateStr),
                        }}
                        errors={formik.errors.date_from}
                        touched={formik.touched.date_from}
                      />
                    </div>
                    <div>
                      <PMDatePicker
                        triggerVariant="design"
                        accentColor={ACCENT.solid}
                        name="date_too"
                        label={t("Dato til")}
                        value={formik.values.date_too}
                        disabled={datesDisabled}
                        dayInfoMap={holidayDayInfoMap}
                        onOpen={() => {
                          const year = formik.values.date_too
                            ? parseInt(String(formik.values.date_too).substring(0, 4), 10)
                            : formik.values.date_from
                              ? parseInt(String(formik.values.date_from).substring(0, 4), 10)
                              : new Date().getFullYear();
                          ensureHolidaysForYear(year);
                        }}
                        onMonthChange={(_month, year) => ensureHolidaysForYear(year)}
                        onChange={(e) => {
                          formik.setFieldValue("date_too", e.target.value);
                          const year = parseInt(String(e.target.value).substring(0, 4), 10);
                          ensureHolidaysForYear(year);
                        }}
                        validationRules={{
                          minDate: formik.values.date_from || dateToday,
                          startDate: formik.values.date_from,
                          maxDays: absencePickerMaxDays(selectedAbsenceType),
                          unlimited: isUnlimitedAbsenceDuration(selectedAbsenceType),
                          userId: formik.values.user_id,
                          isDateValid: (dateStr) =>
                            isAbsenceEndDateValid({
                              dateStr,
                              dateFrom: formik.values.date_from,
                              absenceType: selectedAbsenceType,
                              isPublicHolidayDate,
                              rangeIncludesPublicHoliday,
                              userId: formik.values.user_id,
                            }),
                        }}
                        errors={formik.errors.date_too}
                        touched={formik.touched.date_too}
                      />
                      {(() => {
                        if (isUnlimitedAbsenceDuration(selectedAbsenceType) && !isSickLeaveAbsenceType(selectedAbsenceType)) {
                          return (
                            <p style={{ ...dsHintStyle, fontStyle: "italic" }}>
                              {t("absence.edit.comp_time_hint")}
                            </p>
                          );
                        }
                        if (isSickLeaveAbsenceType(selectedAbsenceType)) {
                          return (
                            <p style={{ ...dsHintStyle, fontStyle: "italic" }}>
                              {t("absence.edit.sick_leave_hint")}
                            </p>
                          );
                        }
                        const maxDaysHint = absencePickerMaxDays(selectedAbsenceType);
                        return maxDaysHint ? (
                          <p style={dsHintStyle}>
                            {maxDaysHint === 1
                              ? t("absence.edit.max_days_hint_one")
                              : t("absence.edit.max_days_hint_other", { count: maxDaysHint })}
                          </p>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  {(!formik.values.user_id || !formik.values.absence_type_id) && (
                    <p style={dsHintStyle}>
                      {!formik.values.user_id ? t("absence.edit.select_employee_first") : t("absence.edit.select_absence_type_first")}
                    </p>
                  )}
                </div>

                {/* Beskrivelse */}
                <div>
                  <label style={dsLabelStyle}>{t("absence.edit.description")}</label>
                  <div style={{ border: "1.5px solid #E2E8F0", borderRadius: 14, background: "#fff", overflow: "hidden" }}>
                    <QuillEditor
                      name="description"
                      rows={20}
                      {...formik}
                      readOnly={!formik.values.user_id || !formik.values.absence_type_id || isReadOnly}
                    />
                  </div>
                  {(!formik.values.user_id || !formik.values.absence_type_id) && (
                    <p style={dsHintStyle}>
                      {!formik.values.user_id ? t("absence.edit.select_employee_first") : t("absence.edit.select_absence_type_first")}
                    </p>
                  )}
                </div>

                {/* Vedlegg */}
                <div>
                  <label style={dsLabelStyle}>{t("absence.edit.documents_title")}</label>
                  {absence?.id ? (
                    <div style={{ border: "1.5px solid #E2E8F0", borderRadius: 14, background: "#fff", padding: 12 }}>
                      <DocumentCenter
                        subtitle={t("absence.edit.documents_subtitle")}
                        module="absence"
                        allowedFileTypes="application/pdf"
                        moduleParam={absence?.id}
                      />
                    </div>
                  ) : (
                    <>
                      <input
                        ref={pendingAbsenceDocInputRef}
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        accept={ABSENCE_PENDING_DOC_ACCEPT}
                        onChange={(e) => {
                          const list = e.target.files;
                          if (!list?.length) return;
                          const added = [];
                          for (const file of Array.from(list)) {
                            if (!isAbsencePendingDocAllowed(file)) {
                              toast.error(t("absence.edit.documents_subtitle"));
                              continue;
                            }
                            added.push({ key: `${file.name}_${file.size}_${file.lastModified}`, file });
                          }
                          if (added.length > 0) {
                            setPendingAbsenceDocuments((prev) => [...prev, ...added]);
                          }
                          e.target.value = "";
                        }}
                      />
                      <div style={{ border: "1.5px dashed #CBD5E1", borderRadius: 16, padding: 18, background: "#fff", textAlign: "center" }}>
                        <div style={{ width: 46, height: 46, margin: "0 auto 10px", borderRadius: 13, background: ACCENT.soft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ACCENT.solid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" /></svg>
                        </div>
                        <div style={{ fontSize: 13.5, color: "#64748B", lineHeight: 1.5, marginBottom: 14 }}>
                          {t("absence.edit.documents_subtitle")}
                        </div>
                        <button
                          type="button"
                          onClick={() => pendingAbsenceDocInputRef.current?.click()}
                          style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", borderRadius: 12, border: "1.5px solid #E2E8F0", background: "#fff", color: "#334155", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}
                        >
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                          {t("absence.edit.documents_add")}
                        </button>
                      </div>
                      {pendingAbsenceDocuments.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                          {pendingAbsenceDocuments.map((row) => (
                            <div key={row.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 12 }}>
                              <span style={{ width: 38, height: 38, flex: "0 0 auto", borderRadius: 10, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B" }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>
                              </span>
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#0F172A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.file?.name}>
                                  {row.file?.name}
                                </span>
                                <span style={{ display: "block", fontSize: 12.5, color: "#94A3B8", fontWeight: 500 }}>{formatBytes(row.file?.size)}</span>
                              </span>
                              <button
                                type="button"
                                aria-label={t("absence.edit.documents_remove")}
                                onClick={() => setPendingAbsenceDocuments((prev) => prev.filter((x) => x.key !== row.key))}
                                style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", border: "none", background: "transparent", borderRadius: 8, cursor: "pointer", color: "#94A3B8", flex: "0 0 auto" }}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>

                {/* Sticky action bar */}
                <div style={{ display: "flex", gap: 12, position: "sticky", bottom: 0, zIndex: 5, background: "#fff", margin: "8px -16px -16px", padding: "12px 16px", borderTop: "1px solid #EEF1F5" }}>
                  {onClose && (
                    <button
                      type="button"
                      onClick={() => onClose()}
                      style={{ padding: "16px 22px", borderRadius: 14, border: "1.5px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 700, fontSize: 16, cursor: "pointer", fontFamily: "inherit", flex: "0 0 auto" }}
                    >
                      {t("absence.edit.cancel")}
                    </button>
                  )}
                  <button type="submit" disabled={!canSave || formik.isSubmitting} style={saveBtnStyle}>
                    {formik.isSubmitting ? (
                      <span>{t("absence.edit.saving")}</span>
                    ) : (
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                        {absence?.id ? t("absence.edit.update") : t("absence.edit.create")}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </Form>

            {savedOverlay && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 40,
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 32,
                  textAlign: "center",
                  borderRadius: 16,
                  overflowY: "auto",
                }}
              >
                <div
                  style={{
                    width: 88,
                    height: 88,
                    borderRadius: 999,
                    background: ACCENT.soft,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke={ACCENT.solid} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <h2 style={{ margin: "22px 0 6px", fontSize: 24, fontWeight: 800, color: "#0F172A" }}>
                  {t("absence.edit.saved_title")}
                </h2>
                <p style={{ margin: "0 0 22px", fontSize: 14.5, color: "#64748B", maxWidth: 300, lineHeight: 1.5 }}>
                  {savedSummary?.hadDocuments
                    ? t("absence.edit.saved_body_with_documents")
                    : t("absence.edit.saved_body")}
                </p>
                {savedSummary && (
                  <div
                    style={{
                      width: "100%",
                      maxWidth: 330,
                      background: "#F8FAFC",
                      border: "1.5px solid #EEF1F5",
                      borderRadius: 16,
                      padding: "6px 16px",
                      marginBottom: 24,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: "1px solid #EEF1F5" }}>
                      <span style={{ fontSize: 14, color: "#64748B", fontWeight: 600 }}>{t("absence.edit.absence_type")}</span>
                      <span style={{ fontSize: 15, color: "#0F172A", fontWeight: 700, textAlign: "right" }}>{savedSummary.typeLabel}</span>
                    </div>
                    {savedSummary.showEmployee && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: "1px solid #EEF1F5" }}>
                        <span style={{ fontSize: 14, color: "#64748B", fontWeight: 600 }}>{t("absence.edit.employee")}</span>
                        <span style={{ fontSize: 15, color: "#0F172A", fontWeight: 700, textAlign: "right" }}>{savedSummary.employeeLabel}</span>
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: "1px solid #EEF1F5" }}>
                      <span style={{ fontSize: 14, color: "#64748B", fontWeight: 600 }}>{t("absence.edit.period")}</span>
                      <span style={{ fontSize: 15, color: "#0F172A", fontWeight: 700, textTransform: "capitalize" }}>{savedSummary.periodLabel}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0" }}>
                      <span style={{ fontSize: 14, color: "#64748B", fontWeight: 600 }}>{t("absence.edit.status")}</span>
                      <span style={{ fontSize: 15, color: "#B45309", fontWeight: 700 }}>{t("absence.edit.pending_approval")}</span>
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", gap: 12, width: "100%", maxWidth: 330 }}>
                  <button
                    type="button"
                    onClick={() => {
                      const saved = lastSavedAbsenceRef.current;
                      lastSavedAbsenceRef.current = null;
                      setSavedOverlay(false);
                      setSavedSummary(null);
                      if (onChange && saved) onChange(saved);
                      if (onClose) onClose();
                    }}
                    style={{
                      flex: 1,
                      padding: 15,
                      borderRadius: 14,
                      border: "1.5px solid #E2E8F0",
                      background: "#fff",
                      color: "#475569",
                      fontWeight: 700,
                      fontSize: 15,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {t("absence.edit.close")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSavedOverlay(false);
                      setSavedSummary(null);
                      resetFormForNewEntry(formik);
                    }}
                    style={{
                      flex: 1,
                      padding: 15,
                      borderRadius: 14,
                      border: "none",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: 15,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      background: ACCENT.grad,
                      boxShadow: `0 10px 20px -8px ${ACCENT.glow}.6)`,
                    }}
                  >
                    {t("absence.edit.register_new")}
                  </button>
                </div>
              </div>
            )}
          </>
          );
        }}
      </Formik>
    </div>
  );
};

AbsenceForm.propTypes = {
  absenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  prefill: PropTypes.object,
  onChange: PropTypes.func,
  onLoaded: PropTypes.func,
  onClose: PropTypes.func,
  showHeader: PropTypes.bool,
};

export default AbsenceForm;
